import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton } from 'seagull';
import { Box, Column, Row } from 'seagull/components/Layout';
import { ProgressBar } from 'seagull/components/ProgressBar';
import { Typography } from 'seagull/components/Typography';
import { OpenTab } from 'seagull/icons/arrows';
import { PDF } from 'seagull/icons/content-edit';
import { CloseCross } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useLocaleKeys } from '~/i18n';
import { isTerminal } from '../../../taskModel';
import { useTasksDrawer } from '../../../tasksDrawerStore';
import { timeAgo } from '../taskTimeAgo.utils';
import styles from './RelationshipPdfTaskItem.module.scss';
export const GetRelationshipPdfTaskItemTID = (parentTestId)=>getTestIds(parentTestId);
export const RelationshipPdfTaskItem = (param)=>{
    let { task, cancelTask, testId } = param;
    const { t: tasksDrawerTexts } = useLocaleKeys('navigation.tasksDrawer');
    const { browser: { openNewTab } } = useIoContext();
    const { retryAgreementPdf } = useDownloadAgreementPdf();
    const retryAgreementPdfRef = useRef(retryAgreementPdf);
    retryAgreementPdfRef.current = retryAgreementPdf;
    const { openDrawer } = useTasksDrawer();
    const { RelationshipPdfTaskItemTestIds } = useMemo(()=>GetRelationshipPdfTaskItemTID(testId), [
        testId
    ]);
    const [, refreshTimeAgo] = useState(0);
    useEffect(()=>{
        if (!isTerminal(task.status)) return;
        const id = setInterval(()=>refreshTimeAgo((n)=>n + 1), 60000);
        return ()=>clearInterval(id);
    }, [
        task.status
    ]);
    const relationshipId = task.relationshipId;
    const onRemove = useCallback(()=>cancelTask(task.jobId), [
        task.jobId,
        cancelTask
    ]);
    const onRetry = useCallback(()=>{
        if (relationshipId == null) return;
        retryAgreementPdfRef.current(relationshipId, {
            previousJobId: task.jobId
        });
    }, [
        task.jobId,
        relationshipId
    ]);
    const onView = useCallback(()=>{
        if (task.downloadUrl != null) openNewTab(task.downloadUrl);
    }, [
        task.downloadUrl
    ]);
    const showStatusDot = isTerminal(task.status) && !task.seen;
    const titleBlock = /*#__PURE__*/ _jsxs(Row, {
        gap: 6,
        alignItems: "center",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                weight: "bold",
                ellipsis: true,
                testId: RelationshipPdfTaskItemTestIds.Title(),
                children: task.agreementName
            }),
            showStatusDot && /*#__PURE__*/ _jsx(Box, {
                className: styles.statusDot,
                testId: RelationshipPdfTaskItemTestIds.StatusDot()
            })
        ]
    });
    const pageTypeIcon = /*#__PURE__*/ _jsx(Box, {
        className: styles.pageType,
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/ _jsx(PDF, {
            size: 24
        })
    });
    const removeButton = /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'task_item_remove'
        },
        skin: "transparent",
        size: "small",
        tooltip: {
            title: tasksDrawerTexts.relationshipPdf.remove()
        },
        testId: RelationshipPdfTaskItemTestIds.RemoveButton(),
        onClick: onRemove,
        children: /*#__PURE__*/ _jsx(CloseCross, {
            size: 16
        })
    });
    if (task.status === 'failed') {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 16,
            alignItems: "center",
            testId: RelationshipPdfTaskItemTestIds.Root(),
            className: styles.row,
            children: [
                pageTypeIcon,
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.body,
                    children: [
                        titleBlock,
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: RelationshipPdfTaskItemTestIds.Subtitle(),
                            children: tasksDrawerTexts.relationshipPdf.failedSubtitle({
                                timeAgo: timeAgo(task.updatedAt)
                            })
                        })
                    ]
                }),
                relationshipId == null ? /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    bi: {
                        name: 'task_item_view_tasks'
                    },
                    testId: RelationshipPdfTaskItemTestIds.ViewTasksButton(),
                    onClick: openDrawer,
                    children: tasksDrawerTexts.relationshipPdf.viewTasks()
                }) : /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    bi: {
                        name: 'task_item_retry'
                    },
                    testId: RelationshipPdfTaskItemTestIds.RetryButton(),
                    onClick: onRetry,
                    children: tasksDrawerTexts.relationshipPdf.retry()
                }),
                removeButton
            ]
        });
    }
    if (task.status === 'ready') {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 16,
            alignItems: "center",
            testId: RelationshipPdfTaskItemTestIds.Root(),
            className: styles.row,
            children: [
                pageTypeIcon,
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.body,
                    children: [
                        titleBlock,
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: RelationshipPdfTaskItemTestIds.Subtitle(),
                            children: tasksDrawerTexts.relationshipPdf.readySubtitle({
                                timeAgo: timeAgo(task.updatedAt)
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    disabled: task.downloadUrl == null,
                    suffixIcon: /*#__PURE__*/ _jsx(OpenTab, {
                        size: 16
                    }),
                    bi: {
                        name: 'task_item_view'
                    },
                    testId: RelationshipPdfTaskItemTestIds.ViewButton(),
                    onClick: onView,
                    children: tasksDrawerTexts.relationshipPdf.openPdf()
                }),
                removeButton
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        gap: 16,
        alignItems: "center",
        testId: RelationshipPdfTaskItemTestIds.Root(),
        className: styles.row,
        children: [
            pageTypeIcon,
            /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                className: styles.body,
                children: [
                    titleBlock,
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: 8,
                        alignItems: "center",
                        className: styles.progressRow,
                        children: [
                            /*#__PURE__*/ _jsx(ProgressBar, {
                                className: styles.progressBar,
                                completed: task.progress,
                                total: 100,
                                testId: RelationshipPdfTaskItemTestIds.ProgressBarSlot()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                testId: RelationshipPdfTaskItemTestIds.Progress(),
                                children: tasksDrawerTexts.common.percent({
                                    value: task.progress
                                })
                            })
                        ]
                    })
                ]
            }),
            removeButton
        ]
    });
};
